/**
 * Pure logic behind the asset-card evaluation badge.
 *
 * Given an asset's rule verdicts and a lookup of rule metadata, this decides what
 * the card should show: a severity-coloured badge for violated rules and a separate
 * "could not evaluate" badge for failed or never-evaluated assets. Kept free of React
 * and Meridian so the rules are simple to unit test.
 */

import type { EvaluatedRule, RuleMap, RuleSeverity } from 'src/types/assetEval';

export interface BrokenRuleLine {
  ruleId: string;
  name: string;
  severity: RuleSeverity;
  reason?: string;
}

// Failed rules deliberately omit the raw evaluation exception: it is stamped on the verdict
// in the backend for internal viewing, but must not surface to customers in the badge tooltip.
export interface FailedRuleLine {
  ruleId: string;
  name: string;
}

export interface AssetEvalDisplay {
  /** Rules the content violates, sorted most-severe first. */
  violated: BrokenRuleLine[];
  /** Rules that were checked but could not be evaluated. */
  failed: FailedRuleLine[];
  /** True when the asset has no verdicts at all (never went through evaluation). */
  notEvaluated: boolean;
  /** Highest severity among the violated rules, or undefined when none are violated. */
  worstSeverity?: RuleSeverity;
  /** Whether to show the severity-coloured violation badge. */
  hasViolations: boolean;
  /** Whether to show the "could not evaluate" badge (any failed verdict OR never evaluated). */
  hasFailure: boolean;
  /** True when the asset was evaluated and every rule passed (no violations, no failures). */
  passed: boolean;
}

const SEVERITY_RANK: Record<RuleSeverity, number> = {
  BLOCKER: 3,
  WARNING: 2,
  INFO: 1,
};

/**
 * A violated rule whose configuration cannot be resolved is treated as the most
 * severe, so an unknown violation is never visually understated.
 */
const UNRESOLVED_SEVERITY: RuleSeverity = 'BLOCKER';

/**
 * Rule severity for a verdict: prefer the point-in-time snapshot stored on the verdict itself
 * (static to the asset, and correct even if the rule was later renamed, re-classified, or deleted);
 * fall back to the live rule config via the RuleMap for verdicts written before the snapshot existed;
 * and finally treat a still-unresolvable rule as the most severe so a violation is never understated.
 */
function resolveSeverity (rule: EvaluatedRule, ruleMap: RuleMap): RuleSeverity {
  return rule.severity ?? ruleMap.get(rule.ruleId)?.severity ?? UNRESOLVED_SEVERITY;
}

/** Rule display name for a verdict: the verdict's own snapshot first, then the RuleMap, then the raw id. */
function resolveName (rule: EvaluatedRule, ruleMap: RuleMap): string {
  return rule.title ?? ruleMap.get(rule.ruleId)?.name ?? rule.ruleId;
}

export function computeAssetEvalDisplay (
  evaluatedRules: EvaluatedRule[] | undefined,
  ruleMap: RuleMap,
): AssetEvalDisplay {
  // "Nothing to show": the asset never produced a verdict — either no evaluatedRules field
  // at all, or a present-but-empty array (a genuine evaluation always writes at least one
  // verdict, so an empty array means no rule applied / never evaluated). This is neither a
  // pass nor a failure, so the card shows no badge.
  if (evaluatedRules === undefined || evaluatedRules.length === 0) {
    return { violated: [], failed: [], notEvaluated: true, hasViolations: false, hasFailure: false, passed: false };
  }

  const violated: BrokenRuleLine[] = evaluatedRules
    .filter((rule) => rule.status === 'VIOLATED')
    .map((rule) => ({
      ruleId: rule.ruleId,
      name: resolveName(rule, ruleMap),
      severity: resolveSeverity(rule, ruleMap),
      reason: rule.reason,
    }))
    .sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity]);

  const failed: FailedRuleLine[] = evaluatedRules
    .filter((rule) => rule.status === 'FAILED')
    .map((rule) => ({
      ruleId: rule.ruleId,
      name: resolveName(rule, ruleMap),
    }));

  return {
    violated,
    failed,
    notEvaluated: false,
    worstSeverity: violated[0]?.severity,
    hasViolations: violated.length > 0,
    hasFailure: failed.length > 0,
    passed: violated.length === 0 && failed.length === 0,
  };
}

/**
 * Whether an asset should default to reject: it has at least one VIOLATED rule whose severity is
 * not INFO. PASSED and FAILED ("could not evaluate") verdicts never flag, and INFO-severity
 * violations are informational only. A violation with no severity snapshot is treated as flagging
 * (unknown is not INFO), so a rule is never silently downgraded.
 */
export function isAssetFlagged (evaluatedRules: EvaluatedRule[] | undefined): boolean {
  return (evaluatedRules ?? []).some(
    (rule) => rule.status === 'VIOLATED' && rule.severity !== 'INFO',
  );
}

/**
 * Meridian Icon colour for a violation badge of the given worst severity.
 * INFO (or none) has no colour so it reads as a plain, neutral sign.
 */
export function severityIconColor (severity: RuleSeverity | undefined): 'error' | 'warning' | undefined {
  if (severity === 'BLOCKER') return 'error';
  if (severity === 'WARNING') return 'warning';
  return undefined;
}

/** Hover line for one rule: "name: reason", or just "name" when no reason is stored. */
export function formatRuleLine (rule: { name: string; reason?: string }): string {
  return rule.reason ? `${rule.name}: ${rule.reason}` : rule.name;
}
